import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
} from '../SettingsShell/SettingsPage';
import { SetAvatar, SetChip } from '../SettingsShell/controls';
import { ProviderDrawer } from './ProviderDrawer';
import { ROLE_LABEL } from '../../settings-practice/types';
import type { Provider } from '../../settings-practice/types';

export interface ProvidersViewProps {
  providers: Provider[];
  location: string;
  lastSync: string;
  /** Persist a provider saved from the drawer. */
  onSaveProvider?: (next: Provider) => void | Promise<void>;
}

const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Practice management › Providers (spec 15 §5). Roster + the drawer. */
export function ProvidersView({ providers, location, lastSync, onSaveProvider }: ProvidersViewProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Provider | null>(null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.elvaSays.toLowerCase().includes(q) ||
        p.roles.some((r) => ROLE_LABEL[r].toLowerCase().includes(q)),
    );
  }, [providers, query]);

  // Derived from the same array that draws the rows (§3.1).
  const working = providers.filter((p) => p.status !== 'away').length;
  const away = providers.filter((p) => p.status === 'away').length;

  return (
    <div>
      <SettingsPageHeader
        eyebrow="Practice management"
        titleLead="Providers"
        sub={
          <>Who works here and when. The list comes from your practice management system.</>
        }
      />

      <SettingsCards>
        <SettingsCard
          title="The roster"
          sub="Search by name or role."
          headRight={
            <span className="text-[12px] text-[var(--text-muted)]">
              <strong className="text-[var(--text-secondary)]">{working}</strong> working ·{' '}
              <strong className="text-[var(--text-secondary)]">{away}</strong> away · {providers.length} on the roster
            </span>
          }
        >
          <div className="px-6 pt-5">
            <span className="flex items-center gap-2 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2">
              <Icon kind="search" size={14} stroke={2} style={{ color: 'var(--text-muted)' }} />
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); }}
                placeholder="Search by name or role…"
                aria-label="Search providers"
                className="w-full bg-transparent text-[13px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
              />
            </span>
          </div>

          <div className="mt-4 grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.2fr)_150px_120px_140px_28px] gap-x-4 px-6 pb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
            <span>Provider</span>
            <span>Roles</span>
            <span>Which days</span>
            <span className="text-right">Monthly target</span>
            <span className="text-right">Right now</span>
            <span />
          </div>

          {rows.length === 0 ? (
            <div className="px-6 py-10 text-center text-[13px] text-[var(--text-muted)]">
              Nobody on the roster matches that.
            </div>
          ) : (
            rows.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setOpen(p); }}
                className="grid w-full grid-cols-[minmax(0,1.5fr)_minmax(0,1.2fr)_150px_120px_140px_28px] items-center gap-x-4 border-t border-[var(--border-default)] px-6 py-3.5 text-left hover:bg-[var(--surface-sunken)]"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <SetAvatar initials={p.initials} colour={p.colour} />
                  <span className="min-w-0">
                    <span className="block truncate text-[13.5px] font-semibold text-[var(--text-primary)]">
                      {p.name}
                    </span>
                    <span className="block truncate text-[11.5px] text-[var(--text-muted)]">
                      ELVA says &ldquo;{p.elvaSays}&rdquo;
                    </span>
                  </span>
                </span>
                <span className="flex min-w-0 flex-wrap gap-1.5">
                  {p.roles.map((r) => (
                    <SetChip key={r} tone="grey">
                      {ROLE_LABEL[r]}
                    </SetChip>
                  ))}
                </span>
                <span className="flex items-center gap-1">
                  {p.days.map((d, i) => (
                    <span
                      key={d.label}
                      aria-hidden
                      className={cn(
                        'flex h-[18px] w-[18px] items-center justify-center rounded-[5px] text-[10px] font-bold',
                        d.working ? 'text-white' : 'bg-transparent text-[#D8DCE7]',
                      )}
                      style={d.working ? { background: p.colour } : undefined}
                    >
                      {DAY_LETTERS[i]}
                    </span>
                  ))}
                </span>
                <span className="text-right font-[var(--font-mono)] text-[13px] text-[var(--text-secondary)]">
                  ${p.monthlyTarget}
                </span>
                <span className="flex justify-end">
                  <StatusChip p={p} />
                </span>
                <span className="flex justify-end">
                  <Icon kind="right" size={14} stroke={2} style={{ color: 'var(--text-muted)' }} />
                </span>
              </button>
            ))
          )}

          <SettingsNote>
            Providers are added and removed in your practice management system. Here you can stop ELVA offering someone.
          </SettingsNote>
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter
        location={location}
        left={
          <>
            Last sync <strong className="text-[var(--text-secondary)]">{lastSync}</strong>. {providers.length} providers,
            no changes.
          </>
        }
      />

      <ProviderDrawer
        provider={open}
        location={location}
        providers={providers}
        onClose={() => { setOpen(null); }}
        {...(onSaveProvider ? { onSave: onSaveProvider } : {})}
      />
    </div>
  );
}

function StatusChip({ p }: { p: Provider }) {
  if (p.status === 'away') {
    return (
      <SetChip tone="amber">
        Away{p.statusNote ? ` · ${p.statusNote}` : ''}
      </SetChip>
    );
  }
  if (p.status === 'no_new_patients') return <SetChip tone="indigo">No new patients</SetChip>;
  return <SetChip tone="green">Working</SetChip>;
}
